import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'

import { AdminUITheme } from 'types/AdminUiThemeMui'
import PRMActivityNoteCard from 'components/ParticipantProfile/PRMActivityNoteCard'
import { ActivityType } from 'types/ViewActivities'
import { titleCreator } from './Helper'
import PRMActivityNoteFilter from 'components/ParticipantProfile/PRMActivityNoteFilter'
import { ParticipantProfileContext } from 'containers/ParticipantProfile'
import { DEFAULT_PRM_ACTIVITIES_NOTES_COUNT } from 'lib/GlobalConstants'
import useInfiniteScroll from 'lib/hooks/UseInfiniteScroll'
import viewActivitiesAPI from 'api/Participant/ViewActivities/Action'

const ViewActivities = () => {
  const { id } = useParams() as { id: string }
  const [pageNumber, setPageNumber] = useState(1)
  const {
    participantActivities: { activitiesProjectId },
    projects,
  } = useContext(ParticipantProfileContext).state
  const { setProjectIdForActivitiesFilter } = useContext(
    ParticipantProfileContext,
  ).functions

  useEffect(() => {
    setProjectIdForActivitiesFilter('')
  }, [])

  // When ever we change projectId we reset the page to refresh the list
  useEffect(() => {
    setPageNumber(1)
  }, [activitiesProjectId])

  const {
    loading,
    data: activities,
    hasMore,
  } = useInfiniteScroll(viewActivitiesAPI.viewActivities, {
    participantId: id,
    projectId: activitiesProjectId,
    page: pageNumber,
    perPage: DEFAULT_PRM_ACTIVITIES_NOTES_COUNT,
    newNotesCount: 0,
  })

  // We need to create observerRef for tracking last Item in the list
  const observerRef = useRef<any>(null)

  // When ever the last Item is observed this function will run
  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return
      // If we are on the last item of the list we need to disconnect and connect back to the first item of next list
      if (observerRef.current) observerRef.current.disconnect()
      // The Intersection Observer API provides a way to asynchronously observe changes in the intersection of a target
      // element with an ancestor element or with a top-level document's viewport
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPageNumber) => prevPageNumber + 1)
        }
      })
      if (node) observerRef.current.observe(node)
    },
    [loading, hasMore],
  )

  const theme: AdminUITheme = useTheme()
  const { t } = useTranslation()
  const isActivitiesInMultipleOfDefaultCount =
    activities.length === 0 ||
    activities.length % DEFAULT_PRM_ACTIVITIES_NOTES_COUNT === 0

  return (
    <Box
      data-testid="view-activities"
      style={theme.ParticipantGrid.activityNotesBG}
    >
      <PRMActivityNoteFilter
        projects={projects}
        setProjectIdForFilter={setProjectIdForActivitiesFilter}
      />

      {!hasMore && activities.length === 0 && (
        <Typography textAlign="center">
          {t('participant.viewActivities.noActivitiesFound')}
        </Typography>
      )}

      {
        <>
          {activities.map((activity: ActivityType, index: number) => {
            const { id } = activity
            const {
              titleEventName,
              titleProjectName,
              titleToolType,
              titleToolName,
              createdAt,
              toolLink,
              trackableName,
              trackableType,
              projectName,
              icon,
              eventType,
            } = titleCreator(activity)
            if (activities.length === index + 1) {
              return (
                <div ref={lastItemRef} key={id}>
                  <PRMActivityNoteCard
                    key={id}
                    Icon={icon}
                    data-testid="activity-summary"
                    titleEventName={titleEventName}
                    titleToolName={titleToolName}
                    titleProjectName={titleProjectName}
                    titleToolType={titleToolType}
                    createdAt={createdAt}
                    toolLink={toolLink}
                    trackableName={trackableName}
                    trackableType={trackableType}
                    projectName={projectName}
                    eventType={eventType}
                  />
                </div>
              )
            } else {
              return (
                <div key={id}>
                  <PRMActivityNoteCard
                    key={id}
                    Icon={icon}
                    data-testid="activity-summary"
                    titleEventName={titleEventName}
                    titleToolName={titleToolName}
                    titleToolType={titleToolType}
                    titleProjectName={titleProjectName}
                    createdAt={createdAt}
                    toolLink={toolLink}
                    trackableName={trackableName}
                    trackableType={trackableType}
                    projectName={projectName}
                    eventType={eventType}
                  />
                </div>
              )
            }
          })}
          {hasMore && loading && isActivitiesInMultipleOfDefaultCount && (
            <Box style={theme.ParticipantGrid.displayFlexWithCenter}>
              <CircularProgress size={'2.125rem'} />
            </Box>
          )}
        </>
      }
    </Box>
  )
}

export default ViewActivities
